import { apiCall } from '@/lib/api';
import { createMutation, createQuery, useQueryClient } from '@tanstack/solid-query';
import type { CreateAppleUdidBody } from '@uchat-team-management/shared';
import {
  createAppleUdid,
  deleteAppleUdid,
  getMyAppleUdids,
  importFadAppleUdids,
  listAppleUdids,
  reassignAppleUdid,
  registerAppleUdids,
  updateAppleUdid,
  verifyAppleUdids,
} from './api';

export const myUdidsKey = ['apple-udids', 'mine'] as const;
export const allUdidsKey = ['apple-udids', 'all'] as const;

export const useMyAppleUdids = () =>
  createQuery(() => ({
    queryKey: myUdidsKey,
    queryFn: () => apiCall(getMyAppleUdids, {}),
  }));

export const useAllAppleUdids = () =>
  createQuery(() => ({
    queryKey: allUdidsKey,
    queryFn: () => apiCall(listAppleUdids, {}),
  }));

const invalidateUdids = (qc: ReturnType<typeof useQueryClient>) =>
  qc.invalidateQueries({ queryKey: ['apple-udids'] });

export const useAddAppleUdid = () => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (body: CreateAppleUdidBody) => apiCall(createAppleUdid, { body }),
    onSuccess: () => invalidateUdids(qc),
  }));
};

export const useUpdateAppleUdid = () => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (input: { udidId: string; label: string }) =>
      apiCall(updateAppleUdid, { params: { udidId: input.udidId }, body: { label: input.label } }),
    onSuccess: () => invalidateUdids(qc),
  }));
};

export const useDeleteAppleUdid = () => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (udidId: string) => apiCall(deleteAppleUdid, { params: { udidId } }),
    onSuccess: () => invalidateUdids(qc),
  }));
};

export const useImportFadAppleUdids = () => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: () => apiCall(importFadAppleUdids, { body: {} }),
    onSuccess: () => invalidateUdids(qc),
  }));
};

export const useRegisterAppleUdids = () => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: () => apiCall(registerAppleUdids, { body: {} }),
    onSuccess: () => invalidateUdids(qc),
  }));
};

export const useReassignAppleUdid = () => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (input: { udidId: string; userId: string }) =>
      apiCall(reassignAppleUdid, {
        params: { udidId: input.udidId },
        body: { userId: input.userId },
      }),
    onSuccess: () => invalidateUdids(qc),
  }));
};

export const useVerifyAppleUdids = () => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: () => apiCall(verifyAppleUdids, { body: {} }),
    onSuccess: () => invalidateUdids(qc),
  }));
};
